/*
  One canonical audio URL (HAP-4052).

  Every audio surface on the site rewrites the raw S3 link to the
  churchandfamilylife.com/s3 proxy and maps .mp4 to .mp3 — except the mini
  player, which passed `message.audio.url` through untouched.

  That is not just a proxy inconsistency. `components/audio/audio.vue` saves and
  restores playback position under the key `src:${this.src}`, so the page player
  was writing

      src:https://churchandfamilylife.com/s3/…/sermon.mp3

  while the mini player read

      src:https://cfl-mango.s3.amazonaws.com/…/sermon.mp3

  Different key, no stored value, `oncanplay` leaves currentTime at 0 — so
  navigating away from a resource 40 minutes into a sermon restarted it from the
  beginning in the mini player. The handoff this whole feature exists for was
  silently dropping its position every time.

  Keep this the only place the rewrite is written for audio.
*/

const S3_ORIGINS = [
    'https://cfl-mango.s3.amazonaws.com',
    'https://cfl-mango.s3.us-east-1.amazonaws.com',
]

const PROXY_ORIGIN = 'https://churchandfamilylife.com/s3'

/*
  Origin rewrite only, no extension mapping. Images need the same proxy — the
  raw bucket sends no CORS headers, so an offline image fetch against it never
  caches — but obviously must not have `.mp4` turned into `.mp3`.
*/
export const proxyS3 = (url) => {
    if (!url || typeof url !== 'string') return ''
    let out = url
    for (const origin of S3_ORIGINS) out = out.replace(origin, PROXY_ORIGIN)
    return out
}

export const audioSrc = (url) => proxyS3(url).replace('.mp4', '.mp3')

/*
  The audio a record plays: its own audio file, or the smallest mp4 rendition of
  its video (which the proxy serves as .mp3). `reduce` here had no initial value
  in mini-player.vue, so a record whose `downloads` array was present but empty
  after the `.mp4` filter threw "Reduce of empty array with no initial value"
  and blanked the mini player.
*/
export const audioSrcFor = (record) => {
    if (!record) return ''
    if (record.audio?.url) return audioSrc(record.audio.url)
    const renditions = (record.video?.downloads || []).filter((d) => d?.link?.includes('mp4') && Number.isFinite(Number(d?.height)))
    if (!renditions.length) return ''
    const smallest = renditions.reduce((best, d) => (Number(d.height) < Number(best.height) ? d : best))
    return audioSrc(smallest.link)
}

export default audioSrc
